import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
    static targets = [
        'menu',
        'passwordForm',
        'activateFormButton',
        'currentPassword',
        'newPassword',
        'confirmPassword',
        'submitButton',
        'listLoader'
    ]

    private observer: IntersectionObserver | undefined = undefined

    declare readonly menuTarget: HTMLElement
    declare readonly hasMenuTarget: boolean
    declare readonly passwordFormTarget: HTMLElement
    declare readonly hasPasswordFormTarget: boolean
    declare readonly activateFormButtonTarget: HTMLElement
    declare readonly hasActivateFormButtonTarget: boolean
    declare readonly currentPasswordTarget: HTMLElement
    declare readonly hasCurrentPasswordTarget: boolean
    declare readonly newPasswordTarget: HTMLElement
    declare readonly hasNewPasswordTarget: boolean
    declare readonly confirmPasswordTarget: HTMLElement
    declare readonly hasConfirmPasswordTarget: boolean
    declare readonly submitButtonTarget: HTMLElement
    declare readonly hasSubmitButtonTarget: boolean

    connect() {
        this.autoscrollActiveMenuOnMobile()
    }

    listLoaderTargetConnected(element: HTMLElement) {
        this.observer ??= new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.dispatchEvent(new CustomEvent('load-more', { detail: {entry} }))
                }
            })
        })

        this.observer?.observe(element);
    }

    listLoaderTargetDisconnected(element: HTMLElement) {
        this.observer?.unobserve(element)
    }

    activateChangePasswordForm(event: Event) {
        event.preventDefault()
        event.stopPropagation()

        if (!this.hasPasswordFormTarget) {
            return
        }

        if (this.hasActivateFormButtonTarget) {
            this.activateFormButtonTarget.classList.add('hidden')
        }

        if (this.hasNewPasswordTarget) {
            this.newPasswordTarget.classList.remove('hidden')
        }

        if (this.hasConfirmPasswordTarget) {
            this.confirmPasswordTarget.classList.remove('hidden')
        }

        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.classList.remove('hidden')
        }

        this.passwordFormTarget.classList.add('active')
    }

    displayLotView(event: Event) {
        event.preventDefault()
        event.stopPropagation()

        const button = (event.target as HTMLElement).closest('[data-auction]')
        const auctionId = button?.getAttribute('data-auction')

        if (!auctionId) {
            return
        }

        document.dispatchEvent(new CustomEvent('lot-view-modal:open', { detail: { auctionId } }))
    }

    private autoscrollActiveMenuOnMobile() {
        if (window.matchMedia('(min-width: 768px)').matches) {
            return
        }

        if (!this.hasMenuTarget) {
            return
        }

        const menu = this.menuTarget
        const active = this.menuTarget.querySelector('.active') as HTMLElement | null

        if (!active) {
            return
        }

        const padding = 16

        requestAnimationFrame(() => {
            const leftEdge = menu.scrollLeft
            const rightEdge = menu.scrollLeft + menu.clientWidth
            const elLeft = active.offsetLeft
            const elRight = active.offsetLeft + active.offsetWidth

            if (elLeft < leftEdge) {
                const next = Math.max(elLeft - padding, 0)
                menu.scrollTo({ left: next, behavior: 'smooth' })
            } else if (elRight > rightEdge) {
                const next = elRight - menu.clientWidth + padding
                menu.scrollTo({ left: next, behavior: 'smooth' })
            }
        })
    }
}
